import type { FleetPortalTransaction } from "@station/contracts";
import {
  Alert,
  DataTable,
  type DataTableColumn,
  DateInput,
  EmptyState,
  FormField,
  MoneyText,
  QuantityText,
  Select,
  StatusBadge,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ReceiptText } from "lucide-react";
import { type ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { formatPlateDisplay } from "../../features/customers/personal-data-values.js";
import { moneyExport } from "../../features/market/market-values.js";
import { usePortal } from "../../features/portal/portal-context.js";
import {
  type PortalTransactionFilters,
  portalTransactionsQuery,
  usePortalTransactions,
  usePortalVehicles,
} from "../../features/portal/queries.js";
import { PortalHeader } from "../../features/portal/shared.js";
import { compactSearch } from "../../features/shifts/shift-values.js";
import { api } from "../../lib/api.js";
import { useCursorPages } from "../../lib/cursor-pages.js";
import { errorMessage } from "../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../lib/excel.js";
import { formatCount, formatDateTime } from "../../lib/format.js";

const searchSchema = z.object({
  vehicle: z.string().uuid().optional().catch(undefined),
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute("/portal/islemler")({
  validateSearch: (search) => searchSchema.parse(search),
  component: PortalTransactionsRoute,
});

/**
 * Filo işlemleri: kasa satışları ve elle girilen filo fişleri; iptal / iade karşı kayıtları NEGATİF satır olarak
 * ("İptal / iade") görünür. Süzgeçler adreste durur (paylaşılabilir); Excel'e aktarılır.
 */
function PortalTransactionsRoute(): ReactElement {
  const { t } = useTranslation();
  const portal = usePortal();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const vehicles = usePortalVehicles(portal.counterpartyId);
  const filters: PortalTransactionFilters = {
    counterpartyId: portal.counterpartyId,
    vehicleId: search.vehicle,
    from: search.from,
    to: search.to,
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = usePortalTransactions(filters, pages.cursor);
  const setSearch = (patch: Record<string, unknown>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  const columns = useMemo<DataTableColumn<FleetPortalTransaction>[]>(
    () => [
      {
        id: "occurredAt",
        header: t("portal.fields.occurredAt"),
        hideable: false,
        exportValue: (row) => row.occurredAt,
        cell: (row) => <span className="tabular whitespace-nowrap">{formatDateTime(row.occurredAt)}</span>,
      },
      {
        id: "station",
        header: t("portal.fields.station"),
        exportValue: (row) => row.stationName,
        cell: (row) => row.stationName,
      },
      {
        id: "plate",
        header: t("portal.fields.plate"),
        exportValue: (row) => row.plate,
        cell: (row) =>
          row.plate && row.vehicleId ? (
            // Plaka → o aracın işlemleri (süzgeç); satırda odaklanabilir öğe geniş tabloyu klavyeyle kaydırılabilir kılar.
            <Link
              to="/portal/islemler"
              search={(previous) => ({ ...previous, vehicle: row.vehicleId! })}
              className="font-mono underline underline-offset-4"
            >
              {formatPlateDisplay(row.plate)}
              <span className="sr-only"> — {t("portal.vehicles.transactions")}</span>
            </Link>
          ) : row.plate ? (
            <span className="font-mono">{formatPlateDisplay(row.plate)}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: "driver",
        header: t("portal.fields.driver"),
        exportValue: (row) => row.driverName,
        cell: (row) => row.driverName ?? <span className="text-muted-foreground">—</span>,
      },
      {
        id: "product",
        header: t("portal.fields.product"),
        exportValue: (row) => row.productName,
        cell: (row) => row.productName ?? <span className="text-muted-foreground">—</span>,
      },
      {
        id: "quantity",
        header: t("portal.fields.litres"),
        align: "right",
        exportValue: (row) => row.quantity,
        cell: (row) => <QuantityText value={row.quantity} unit="LITRE" />,
      },
      {
        id: "amount",
        header: t("portal.fields.amount"),
        align: "right",
        exportValue: (row) => moneyExport(row.amount),
        cell: (row) => <MoneyText value={row.amount} colorize />,
      },
      {
        id: "odometer",
        header: t("portal.fields.odometer"),
        align: "right",
        defaultVisible: false,
        exportValue: (row) => row.odometerKm,
        cell: (row) =>
          row.odometerKm === null ? "—" : <span className="tabular">{formatCount(row.odometerKm)}</span>,
      },
      {
        id: "kind",
        header: t("portal.fields.kind"),
        exportValue: (row) => t(`portal.transactions.kind.${row.kind}`),
        cell: (row) => (
          <StatusBadge tone={row.kind === "SALE" ? "neutral" : "warning"} className="whitespace-nowrap">
            {t(`portal.transactions.kind.${row.kind}`)}
          </StatusBadge>
        ),
      },
      {
        id: "receipt",
        header: t("portal.fields.receipt"),
        defaultVisible: false,
        exportValue: (row) => row.receiptNo,
        cell: (row) => row.receiptNo ?? <span className="text-muted-foreground">—</span>,
      },
    ],
    [t],
  );

  return (
    <>
      <PortalHeader
        title={t("portal.transactions.title")}
        description={t("portal.transactions.description")}
      />
      <DataTable
        caption={t("portal.transactions.title")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<ReceiptText />}
            title={t("portal.transactions.emptyTitle")}
            description={t("portal.transactions.emptyDescription")}
          />
        }
        filters={
          <>
            <FormField label={t("portal.fields.vehicle")} className="w-44">
              {(control) => (
                <Select
                  {...control}
                  value={search.vehicle ?? ""}
                  onChange={(event) => setSearch({ vehicle: event.target.value || undefined })}
                >
                  <option value="">{t("common.all")}</option>
                  {(vehicles.data ?? []).map((vehicle) => (
                    <option key={vehicle.vehicleId} value={vehicle.vehicleId}>
                      {formatPlateDisplay(vehicle.plate)}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField label={t("portal.fields.from")} className="w-40">
              {(control) => (
                <DateInput
                  {...control}
                  value={search.from ?? ""}
                  onValueChange={(value) => setSearch({ from: value ?? undefined })}
                />
              )}
            </FormField>
            <FormField label={t("portal.fields.to")} className="w-40">
              {(control) => (
                <DateInput
                  {...control}
                  value={search.to ?? ""}
                  onValueChange={(value) => setSearch({ to: value ?? undefined })}
                />
              )}
            </FormField>
          </>
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
        onExport={async (visible) => {
          const rows = await fetchAllPages<FleetPortalTransaction>(
            async (cursor) =>
              (
                await api.GET("/fleet-portal/transactions", {
                  params: { query: portalTransactionsQuery(filters, cursor, 200) as never },
                })
              ).data!,
          );
          await exportRowsToExcel({
            fileName: `filo-islemler-${portal.account.counterpartyCode}`,
            sheetName: t("portal.transactions.title"),
            columns: visible,
            rows,
          });
        }}
      />
    </>
  );
}
